import UnloadHandler from "@components/unload_handler";
import { Body, SectionHeader } from "@sprout/typography";
import { Location } from "@components/ui/location_input";
import { useTranslation } from "react-i18next";
import Container from "@components/ui/container.tsx";
import BackButton from "@components/back_button.tsx";
import "./work_preferences.scss";
import { type Option } from "@sprout/select";
import useGoBack from "@/hooks/use_go_back";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import { useWorkPreferencesForm } from "@hooks/use_work_preferences_form.ts";
import WorkPreferencesForm from "@components/work_preferences/work_preferences_form.tsx";

type Props = {
  employmentTypes: Option[];
  worksites: Option[];
  currentLanguages: Option[];
  currentEmploymentTypes: Option[];
  currentWorksites: Option[];
  currentRelocation: boolean | null;
  currentLocation: Location | null;
  currentSalary: string | null;
  paths: {
    workPreferencesPath: string;
  };
};

const WorkPreferences = ({
  currentEmploymentTypes,
  currentLanguages,
  currentLocation,
  currentRelocation,
  currentWorksites,
  currentSalary,
  paths,
  ...props
}: Props) => {
  const { t } = useTranslation("user");
  const goBack = useGoBack();
  const form = useWorkPreferencesForm(
    paths.workPreferencesPath,
    currentEmploymentTypes,
    currentLanguages,
    currentLocation,
    currentRelocation,
    currentWorksites,
    currentSalary,
  );

  const handleSubmit = () => {
    form.submit().then((success: boolean) => {
      if (success) {
        goBack();
      }
    });
  };

  return (
    <Container>
      {form.unsavedChanges && <UnloadHandler />}
      <BackButton href="/profile" />
      <div className="flex flex-col gap-[4px]">
        <SectionHeader as="h1">{t("workPreferences.header")}</SectionHeader>
        <Body>{t("workPreferences.description")}</Body>
      </div>
      <WorkPreferencesForm
        form={form}
        className="work-preference-inputs"
        languagesHelperText={t("workPreferences.languages.helperText")}
        worksiteOptions={props.worksites}
        employmentTypeOptions={props.employmentTypes}
        locationHelperText={t("workPreferences.locationHelperText")}
      />
      <div className="submit-buttons-stack">
        <PrimaryButton onClick={handleSubmit} loading={form.loading}>
          {t("workPreferences.save")}
        </PrimaryButton>
        <SecondaryButton onClick={goBack}>{t("workPreferences.cancel")}</SecondaryButton>
        {form.error && (
          <p
            id="work-preferences-error"
            className="helper-text helper-text--error"
            aria-live="polite"
          >
            {form.error}
          </p>
        )}
      </div>
    </Container>
  );
};

export default WorkPreferences;
